import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BuzzApiClient } from '../services/buzz-api'
import {
  BuzzBootstrapInput,
  BuzzBootstrapResponse,
  BuzzChannel,
  BuzzThread,
  BuzzTimelineEvent,
} from '../types'
import { buzzBootstrapSignature } from '../services/buzz-bootstrap-input'

const POLL_INTERVAL_MS = 3000

export interface BuzzMessagingState {
  session: BuzzBootstrapResponse | null
  selectedChannelId: string | null
  selectedThreadId: string | null
  events: BuzzTimelineEvent[]
  loading: boolean
  publishing: boolean
  connectionState: 'connecting' | 'ready' | 'reconnecting' | 'error'
  error: string | null
  selectChannel: (channelId: string) => void
  selectThread: (threadId: string) => void
  createChannel: (name: string) => Promise<BuzzChannel>
  createThread: (title: string) => Promise<BuzzThread>
  publish: (content: string, agentSubjectIds?: string[]) => Promise<void>
  refresh: () => Promise<void>
}

function requestId(prefix: 'channel' | 'thread' | 'message'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

type EventLoadSnapshot = {
  input: BuzzBootstrapInput
  contextKey: string
  generation: number
}

export function useBuzzMessaging(
  input: BuzzBootstrapInput | null,
  suppliedClient?: BuzzApiClient,
): BuzzMessagingState {
  const client = useMemo(() => suppliedClient || new BuzzApiClient(), [suppliedClient])
  const contextKey = buzzBootstrapSignature(input)
  const inputRef = useRef(input)
  const contextKeyRef = useRef(contextKey)
  inputRef.current = input
  contextKeyRef.current = contextKey

  const [stateContextKey, setStateContextKey] = useState(contextKey)
  const [session, setSession] = useState<BuzzBootstrapResponse | null>(null)
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null)
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null)
  const [events, setEvents] = useState<BuzzTimelineEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [connectionState, setConnectionState] = useState<BuzzMessagingState['connectionState']>('connecting')
  const [error, setError] = useState<string | null>(null)

  const mounted = useRef(true)
  const identityKeyRef = useRef<string | null>(null)
  const selectedChannelRef = useRef<string | null>(null)
  const selectedThreadRef = useRef<string | null>(null)
  const contextGeneration = useRef(0)
  const eventLoadSequence = useRef(0)
  const publishSequence = useRef(0)
  const lastLoadFailed = useRef(false)
  const pendingChannelRequests = useRef(new Map<string, string>())
  const pendingThreadRequests = useRef(new Map<string, string>())
  const pendingPublishRequests = useRef(new Map<string, string>())

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const isCurrent = useCallback((snapshot: EventLoadSnapshot) => (
    mounted.current
    && contextKeyRef.current === snapshot.contextKey
    && contextGeneration.current === snapshot.generation
  ), [])

  const loadEvents = useCallback(async (
    threadId: string,
    suppliedSnapshot?: EventLoadSnapshot,
  ) => {
    const activeInput = suppliedSnapshot?.input || inputRef.current
    if (!activeInput) return
    const snapshot: EventLoadSnapshot = suppliedSnapshot || {
      input: activeInput,
      contextKey: contextKeyRef.current,
      generation: contextGeneration.current,
    }
    const sequence = ++eventLoadSequence.current
    if (lastLoadFailed.current && isCurrent(snapshot)) setConnectionState('reconnecting')
    try {
      const next = await client.listEvents(activeInput.organization.id, threadId)
      if (!isCurrent(snapshot) || sequence !== eventLoadSequence.current) return
      if (selectedThreadRef.current !== threadId) return
      const recovered = lastLoadFailed.current
      lastLoadFailed.current = false
      setEvents(next)
      if (recovered) setError(null)
      setConnectionState('ready')
    } catch (reason: any) {
      if (!isCurrent(snapshot) || sequence !== eventLoadSequence.current) return
      if (selectedThreadRef.current !== threadId) return
      lastLoadFailed.current = true
      setConnectionState('error')
      setError(reason?.message || 'Failed to load task-thread events.')
    }
  }, [client, isCurrent])

  const refresh = useCallback(async () => {
    const activeInput = inputRef.current
    const activeContextKey = contextKeyRef.current
    const generation = ++contextGeneration.current
    const nextIdentityKey = activeInput
      ? `${activeInput.organization.id}:${activeInput.currentUser.id}`
      : null
    const identityChanged = nextIdentityKey !== identityKeyRef.current
    const preferredThreadId = nextIdentityKey && nextIdentityKey === identityKeyRef.current
      ? selectedThreadRef.current
      : null
    const preferredChannelId = nextIdentityKey && nextIdentityKey === identityKeyRef.current
      ? selectedChannelRef.current
      : null
    if (identityChanged) {
      pendingChannelRequests.current.clear()
      pendingThreadRequests.current.clear()
      pendingPublishRequests.current.clear()
    }
    identityKeyRef.current = nextIdentityKey
    eventLoadSequence.current += 1
    publishSequence.current += 1
    lastLoadFailed.current = false
    setStateContextKey(activeContextKey)
    setSession(null)
    selectedChannelRef.current = preferredChannelId
    setSelectedChannelId(preferredChannelId)
    selectedThreadRef.current = preferredThreadId
    setSelectedThreadId(preferredThreadId)
    setEvents([])
    setPublishing(false)
    setError(null)

    if (!activeInput) {
      selectedChannelRef.current = null
      setSelectedChannelId(null)
      selectedThreadRef.current = null
      setSelectedThreadId(null)
      setLoading(false)
      setConnectionState('error')
      setError('Organization and current user are required.')
      return
    }

    const snapshot = { input: activeInput, contextKey: activeContextKey, generation }
    setLoading(true)
    setConnectionState('connecting')
    try {
      const next = await client.bootstrap(activeInput)
      if (!isCurrent(snapshot)) return
      const nextChannelId = preferredChannelId
        && next.channels.some((channel) => channel.id === preferredChannelId)
        ? preferredChannelId
        : next.channels.find((channel) => channel.isDefault)?.id || next.channels[0]?.id || null
      const nextThreadId = preferredThreadId
        && next.threads.some((thread) => (
          thread.id === preferredThreadId && thread.channelId === nextChannelId
        ))
        ? preferredThreadId
        : next.threads.find((thread) => thread.channelId === nextChannelId)?.id || null
      setSession(next)
      selectedChannelRef.current = nextChannelId
      setSelectedChannelId(nextChannelId)
      selectedThreadRef.current = nextThreadId
      setSelectedThreadId(nextThreadId)
      setConnectionState(next.connection.state === 'ready' ? 'ready' : 'reconnecting')
      if (nextThreadId) await loadEvents(nextThreadId, snapshot)
    } catch (reason: any) {
      if (!isCurrent(snapshot)) return
      setSession(null)
      selectedChannelRef.current = null
      setSelectedChannelId(null)
      selectedThreadRef.current = null
      setSelectedThreadId(null)
      setEvents([])
      setError(reason?.message || 'Failed to connect to Equa Chat.')
      setConnectionState('error')
    } finally {
      if (isCurrent(snapshot)) setLoading(false)
    }
  }, [client, isCurrent, loadEvents])

  useEffect(() => { void refresh() }, [contextKey, refresh])

  useEffect(() => {
    if (!selectedThreadId || stateContextKey !== contextKey) return
    const interval = setInterval(() => { void loadEvents(selectedThreadId) }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [contextKey, loadEvents, selectedThreadId, stateContextKey])

  const selectThread = useCallback((threadId: string) => {
    const thread = session?.threads.find((candidate) => candidate.id === threadId)
    if (!thread || thread.channelId !== selectedChannelRef.current) return
    selectedThreadRef.current = threadId
    eventLoadSequence.current += 1
    setSelectedThreadId(threadId)
    setEvents([])
    setError(null)
    void loadEvents(threadId)
  }, [loadEvents, session])

  const selectChannel = useCallback((channelId: string) => {
    if (!session?.channels.some((channel) => channel.id === channelId)) return
    const nextThreadId = session.threads.find((thread) => thread.channelId === channelId)?.id || null
    selectedChannelRef.current = channelId
    setSelectedChannelId(channelId)
    selectedThreadRef.current = nextThreadId
    eventLoadSequence.current += 1
    setSelectedThreadId(nextThreadId)
    setEvents([])
    setError(null)
    if (nextThreadId) void loadEvents(nextThreadId)
  }, [loadEvents, session])

  const createChannel = useCallback(async (name: string) => {
    const activeInput = inputRef.current
    if (!activeInput) throw new Error('Organization and current user are required.')
    const snapshot = {
      input: activeInput,
      contextKey: contextKeyRef.current,
      generation: contextGeneration.current,
    }
    const requestKey = JSON.stringify([activeInput.organization.id, name])
    const clientRequestId = pendingChannelRequests.current.get(requestKey) || requestId('channel')
    pendingChannelRequests.current.set(requestKey, clientRequestId)
    setError(null)
    try {
      const channel = await client.createChannel(activeInput.organization.id, {
        name,
        description: '',
        channelType: 'stream',
        visibility: 'private',
      }, clientRequestId)
      pendingChannelRequests.current.delete(requestKey)
      if (isCurrent(snapshot)) {
        setSession((current) => current
          ? {
            ...current,
            channels: current.channels.some((candidate) => candidate.id === channel.id)
              ? current.channels.map((candidate) => candidate.id === channel.id ? channel : candidate)
              : [...current.channels, channel],
          } : current)
        selectedChannelRef.current = channel.id
        setSelectedChannelId(channel.id)
        selectedThreadRef.current = null
        setSelectedThreadId(null)
        setEvents([])
      }
      return channel
    } catch (reason: any) {
      if (isCurrent(snapshot)) setError(reason?.message || 'Failed to create the channel.')
      throw reason
    }
  }, [client, isCurrent])

  const createThread = useCallback(async (title: string) => {
    const activeInput = inputRef.current
    const activeChannelId = selectedChannelRef.current
    if (!activeInput) throw new Error('Organization and current user are required.')
    if (!activeChannelId) throw new Error('Select a collaboration channel first.')
    const snapshot = {
      input: activeInput,
      contextKey: contextKeyRef.current,
      generation: contextGeneration.current,
    }
    const requestKey = JSON.stringify([activeInput.organization.id, activeChannelId, title])
    const clientRequestId = pendingThreadRequests.current.get(requestKey) || requestId('thread')
    pendingThreadRequests.current.set(requestKey, clientRequestId)
    setError(null)
    try {
      const thread = await client.createThreadInChannel(
        activeInput.organization.id, activeChannelId, title, clientRequestId,
      )
      pendingThreadRequests.current.delete(requestKey)
      if (isCurrent(snapshot)) {
        setSession((current) => current
          ? {
            ...current,
            threads: current.threads.some((candidate) => candidate.id === thread.id)
              ? current.threads.map((candidate) => candidate.id === thread.id ? thread : candidate)
              : [thread, ...current.threads],
          } : current)
        selectedThreadRef.current = thread.id
        eventLoadSequence.current += 1
        setSelectedThreadId(thread.id)
        setEvents([])
      }
      return thread
    } catch (reason: any) {
      if (isCurrent(snapshot)) setError(reason?.message || 'Failed to create the task thread.')
      throw reason
    }
  }, [client, isCurrent])

  const publish = useCallback(async (content: string, agentSubjectIds: string[] = []) => {
    const activeInput = inputRef.current
    const activeThreadId = selectedThreadRef.current
    if (!activeInput || !session || stateContextKey !== contextKeyRef.current || !activeThreadId) {
      throw new Error('Select a task thread first.')
    }
    const snapshot = {
      input: activeInput,
      contextKey: contextKeyRef.current,
      generation: contextGeneration.current,
    }
    const exactAgentIds = [...new Set(agentSubjectIds.filter(Boolean))]
    const mentions = exactAgentIds.map((subjectId) => ({ subjectType: 'agent' as const, subjectId }))
    const requestKey = JSON.stringify([
      activeInput.organization.id, activeThreadId, content, exactAgentIds,
    ])
    const clientRequestId = pendingPublishRequests.current.get(requestKey) || requestId('message')
    pendingPublishRequests.current.set(requestKey, clientRequestId)
    const sequence = ++publishSequence.current
    setPublishing(true)
    setError(null)
    try {
      await client.publish(
        activeInput.organization.id,
        activeThreadId,
        content,
        mentions,
        clientRequestId,
      )
      pendingPublishRequests.current.delete(requestKey)
      if (isCurrent(snapshot) && selectedThreadRef.current === activeThreadId) {
        await loadEvents(activeThreadId, snapshot)
      }
    } catch (reason: any) {
      if (isCurrent(snapshot)) setError(reason?.message || 'Failed to publish message.')
      throw reason
    } finally {
      if (isCurrent(snapshot) && sequence === publishSequence.current) setPublishing(false)
    }
  }, [client, isCurrent, loadEvents, session, stateContextKey])

  const contextIsCurrent = stateContextKey === contextKey
  return {
    session: contextIsCurrent ? session : null,
    selectedChannelId: contextIsCurrent ? selectedChannelId : null,
    selectedThreadId: contextIsCurrent ? selectedThreadId : null,
    events: contextIsCurrent ? events : [],
    loading: contextIsCurrent ? loading : true,
    publishing: contextIsCurrent ? publishing : false,
    connectionState: contextIsCurrent ? connectionState : 'connecting',
    error: contextIsCurrent ? error : null,
    selectChannel, selectThread, createChannel, createThread, publish, refresh,
  }
}
